import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError } from "@/api/client";
import { produitsService } from "@/api/services";
import { StatusBadge } from "@/components/common/StatusBadge";
import type { ProductDetail } from "@/types/domain";

const afficherValeur = (valeur: string | number | null | undefined) =>
  valeur ?? "—";

export default function ProduitDetailPage() {
  const { produitId } = useParams<{ produitId: string }>();

  const [produit, setProduit] = useState<ProductDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!produitId) {
      setError("Identifiant produit manquant.");
      setLoading(false);
      return;
    }

    const loadProduit = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await produitsService.detail(produitId);
        setProduit(data);
      } catch (err) {
        console.error("Erreur chargement fiche produit :", err);

        if (err instanceof ApiError && err.status === 404) {
          setError("Produit introuvable.");
        } else {
          setError("Impossible de charger la fiche produit.");
        }
      } finally {
        setLoading(false);
      }
    };

    loadProduit();
  }, [produitId]);

  if (loading) {
    return <div className="p-6">Chargement de la fiche produit...</div>;
  }

  if (error || !produit) {
    return (
      <div className="space-y-4 p-6">
        <p>{error ?? "Produit introuvable."}</p>
        <Link to="/produits" className="text-sm font-medium hover:underline">
          Retour aux produits
        </Link>
      </div>
    );
  }

  const prix = Array.isArray(produit.prix) ? produit.prix : [];
  const fournisseurs = Array.isArray(produit.fournisseurs)
    ? produit.fournisseurs
    : [];

  return (
    <div className="space-y-6 p-6" data-testid="produit-detail-page">
      <div className="space-y-3">
        <Link
          to="/produits"
          className="inline-flex text-sm font-medium text-muted-foreground hover:text-foreground hover:underline"
        >
          ← Retour aux produits
        </Link>

        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">
              {produit.description_officielle}
            </h1>
            <p className="font-mono text-sm text-muted-foreground">
              {produit.sku}
            </p>
          </div>

          <StatusBadge
            status={produit.actif ? "Actif" : "Inactif"}
            tone={produit.actif ? "success" : "neutral"}
          />
        </div>
      </div>

      <section className="space-y-4 rounded-lg border border-border bg-card p-5">
        <div>
          <h2 className="text-lg font-semibold">Informations produit</h2>
          <p className="text-sm text-muted-foreground">
            Données générales du catalogue.
          </p>
        </div>

        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <dt className="text-xs font-medium uppercase text-muted-foreground">
              SKU
            </dt>
            <dd className="mt-1 font-mono">{produit.sku}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase text-muted-foreground">
              Famille
            </dt>
            <dd className="mt-1">{afficherValeur(produit.famille)}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase text-muted-foreground">
              Unité de mesure
            </dt>
            <dd className="mt-1">{produit.unite_mesure}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase text-muted-foreground">
              Coût de référence
            </dt>
            <dd className="mt-1 tabular-nums">
              {afficherValeur(produit.cout_reference)}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase text-muted-foreground">
              Emplacement
            </dt>
            <dd className="mt-1">{afficherValeur(produit.emplacement)}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase text-muted-foreground">
              Statut
            </dt>
            <dd className="mt-1">{produit.actif ? "Actif" : "Inactif"}</dd>
          </div>
        </dl>
      </section>

      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Résumé du stock</h2>
          <p className="text-sm text-muted-foreground">
            Quantités fournies directement par le backend.
          </p>
        </div>

        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {[
            ["Stock physique", produit.quantite_physique],
            ["Réservé", produit.quantite_reservee],
            ["Disponible", produit.quantite_disponible],
            ["En commande", produit.quantite_en_commande],
            ["Stock projeté", produit.stock_projete],
            ["Stock minimum", produit.stock_minimum],
          ].map(([libelle, valeur]) => (
            <div key={libelle} className="rounded-lg border border-border p-4">
              <dt className="text-xs font-medium text-muted-foreground">
                {libelle}
              </dt>
              <dd className="mt-2 text-2xl font-semibold tabular-nums">
                {valeur}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Paliers de prix</h2>
          <p className="text-sm text-muted-foreground">
            Prix configurés selon les quantités.
          </p>
        </div>

        {prix.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Aucun palier de prix.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="border-b border-border">
                  <th className="px-4 py-3 text-right">Quantité minimum</th>
                  <th className="px-4 py-3 text-right">Quantité maximum</th>
                  <th className="px-4 py-3 text-right">Prix unitaire</th>
                  <th className="px-4 py-3 text-left">Statut</th>
                </tr>
              </thead>
              <tbody>
                {prix.map((palier, index) => (
                  <tr
                    key={`${palier.quantite_minimum}-${index}`}
                    className="border-b border-border last:border-b-0"
                  >
                    <td className="px-4 py-3 text-right tabular-nums">
                      {palier.quantite_minimum}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {afficherValeur(palier.quantite_maximum)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {palier.prix_unitaire}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge
                        status={palier.actif ? "Actif" : "Inactif"}
                        tone={palier.actif ? "success" : "neutral"}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Fournisseurs</h2>
          <p className="text-sm text-muted-foreground">
            Fournisseurs associés à ce produit.
          </p>
        </div>

        {fournisseurs.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Aucun fournisseur associé.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="border-b border-border">
                  <th className="px-4 py-3 text-left">Nom</th>
                  <th className="px-4 py-3 text-left">Code fournisseur</th>
                  <th className="px-4 py-3 text-left">Référence fournisseur</th>
                  <th className="px-4 py-3 text-right">Coût fournisseur</th>
                  <th className="px-4 py-3 text-right">Délai jours</th>
                  <th className="px-4 py-3 text-left">Principal</th>
                  <th className="px-4 py-3 text-left">Statut</th>
                </tr>
              </thead>
              <tbody>
                {fournisseurs.map((fournisseur) => (
                  <tr
                    key={fournisseur.id}
                    className="border-b border-border last:border-b-0"
                  >
                    <td className="px-4 py-3 font-medium">
                      {fournisseur.nom}
                    </td>
                    <td className="px-4 py-3 font-mono">
                      {afficherValeur(fournisseur.code_fournisseur)}
                    </td>
                    <td className="px-4 py-3">
                      {afficherValeur(fournisseur.reference_fournisseur)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {afficherValeur(fournisseur.cout_fournisseur)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {afficherValeur(fournisseur.delai_jours)}
                    </td>
                    <td className="px-4 py-3">
                      {fournisseur.fournisseur_principal ? "Oui" : "Non"}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge
                        status={fournisseur.actif ? "Actif" : "Inactif"}
                        tone={fournisseur.actif ? "success" : "neutral"}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
